import { LogInBackground } from '../../components/LogInBackground';
import leftSection from '../../components/left_section.png';
import { useForm, SubmitHandler, Control, FormState, UseFormHandleSubmit } from 'react-hook-form';
import {
  FormsInputDropdown,
  FormsMultilineInput,
  InputDropdownItem,
  Button,
  Accordion,
  Spinner,
} from '@platform/components';
import { GreenCircleCheckmark } from '../../../svg/GreenCircleCheckmark';
import { Organizations } from '@platform/client';
import { useEffect } from 'react';
import { useParams } from 'wouter';
import { navigate } from 'wouter/use-browser-location';
import { ROUTES } from '../../../routes/consts';
import { AuthedPage } from '../../../app';

const CHALLENGE_ITEMS = [
  'Product Market Fit',
  'Branding & Positioning',
  'Marketing',
  'Sales',
  'Customer Success',
  'Reporting & Communicating Impact',
  'Fundraising Preparation',
  'Human Resources',
  'Business Operations',
  'Accounting & Finance',
  'Legal',
];

const challengeDropdownItems: InputDropdownItem[] = CHALLENGE_ITEMS.map((item) => ({
  text: item,
  textValue: item,
}));

const CHALLENGE_ERROR_TEXT = 'Select a challenge your organization needs help with to continue';
const CONTEXT_ERROR_TEXT = 'Tell us more about this challenge to continue';
const CONTEXT_INPUT_LABEL =
  'Tell us more about this challenge or opportunity. Why does it matter now?';
const CONTEXT_INPUT_PLACEHOLDER = `Write a few sentences about what you're working through...`;
const CONTEXT_CHARACTER_LIMIT = 700;

const BUSINESS_CHALLENGES_HREF = `/platform${ROUTES.BUSINESS_CHALLENGES}`;

type FormData = {
  challengeOne: string | null;
  contextOne: string | null;
  challengeTwo: string | null;
  contextTwo: string | null;
  challengeThree: string | null;
  contextThree: string | null;
};

type FormPage = 'one' | 'two' | 'three' | 'submitting' | 'success' | 'error';

const BusinessChallengesForm = () => {
  const { page } = useParams<{ page: FormPage }>();
  const paramPage = page || 'one';
  const currentLocalFormData = getLocalFormData();
  const currentPage = getPageByData({ paramPage, currentLocalFormData });
  useEffect(() => {
    navigate(`${BUSINESS_CHALLENGES_HREF}/${currentPage}`, { replace: true });
  }, [currentPage]);

  const { control, formState, handleSubmit } = useForm<FormData>({
    defaultValues: {
      ...currentLocalFormData,
    },
  });

  const headerContent = getHeaderContent(currentPage);
  const exampleContent = getExampleContent(currentPage);

  return (
    <AuthedPage>
      <div className='fixed inset-0 z-50 bg-white'>
        <div className='absolute inset-x-0 top-0 bottom-16'>
          <LogInBackground
            background={leftSection}
            includeLogo={false}
            backgroundRepeat='repeat'
            alignContentCenter={false}
          >
            <div className='pt-8 px-4 md:w-3/4'>
              {headerContent?.headerText && (
                <h2 className='text-display-xs'>{headerContent.headerText}</h2>
              )}
              {headerContent?.subheaderText && (
                <p className='mt-4'>{headerContent.subheaderText}</p>
              )}
              <form className='flex flex-col mt-8 gap-1'>
                {currentPage === 'one' && (
                  <PageOneQuestions control={control} formState={formState} />
                )}
                {currentPage === 'two' && (
                  <PageTwoQuestions control={control} formState={formState} />
                )}
                {currentPage === 'three' && (
                  <PageThreeQuestions control={control} formState={formState} />
                )}
                {currentPage === 'submitting' && (
                  <div className='flex flex-col gap-8 items-center mt-4'>
                    <p className='text-lg text-center'>Submitting your challenges...</p>
                    <Spinner />
                  </div>
                )}
                {currentPage === 'success' && (
                  <div className='flex flex-col items-center'>
                    <GreenCircleCheckmark size={220} />
                    <p className='text-lg text-center mt-4'>
                      Your challenges have been submitted. We will follow up with your hand-picked
                      recommendations within 10 days.
                    </p>
                  </div>
                )}
                {currentPage === 'error' && (
                  <p className='text-lg text-center mt-4'>
                    Something went wrong. Please try again later.
                  </p>
                )}
              </form>
              {exampleContent?.exampleText && exampleContent?.exampleHeader && (
                <div className='py-4'>
                  <Accordion
                    type='custom'
                    className='h-12 py-1 px-4 bg-gray-25 text-gray-500 font-medium rounded-lg'
                    titleContent={exampleContent.exampleHeader}
                    initiallyClosed={true}
                  >
                    <div className='bg-white p-4 text-gray-700 rounded-lg'>
                      {exampleContent.exampleText}
                    </div>
                  </Accordion>
                </div>
              )}
            </div>
          </LogInBackground>
        </div>
        <FormNavigation
          currentPage={currentPage}
          handleSubmit={handleSubmit}
          formState={formState}
        />
      </div>
    </AuthedPage>
  );
};

const PageOneQuestions = (props: {
  control: Control<FormData>;
  formState: FormState<FormData>;
}) => {
  return (
    <>
      <FormsInputDropdown
        control={props.control}
        name='challengeOne'
        rules={{ required: CHALLENGE_ERROR_TEXT }}
        label='First challenge'
        placeholder='Select one'
        items={challengeDropdownItems}
        type='default'
        error={props.formState.errors.challengeOne}
        inputDisabled
      />
      <FormsMultilineInput
        control={props.control}
        name='contextOne'
        rules={{
          required: CONTEXT_ERROR_TEXT,
          maxLength: CONTEXT_CHARACTER_LIMIT,
        }}
        label={CONTEXT_INPUT_LABEL}
        aria-label='Tell us more about this challenge'
        placeholder={CONTEXT_INPUT_PLACEHOLDER}
        formsError={props.formState.errors.contextOne}
        characterCount={CONTEXT_CHARACTER_LIMIT}
      />
    </>
  );
};

const PageTwoQuestions = (props: {
  control: Control<FormData>;
  formState: FormState<FormData>;
}) => {
  return (
    <>
      <FormsInputDropdown
        control={props.control}
        name='challengeTwo'
        rules={{ required: CHALLENGE_ERROR_TEXT }}
        label='Second business challenge'
        placeholder='Select one'
        items={challengeDropdownItems}
        type='default'
        error={props.formState.errors.challengeTwo}
        inputDisabled
      />
      <FormsMultilineInput
        control={props.control}
        name='contextTwo'
        rules={{
          required: CONTEXT_ERROR_TEXT,
          maxLength: CONTEXT_CHARACTER_LIMIT,
        }}
        label={CONTEXT_INPUT_LABEL}
        aria-label='Tell us more about this challenge'
        placeholder={CONTEXT_INPUT_PLACEHOLDER}
        formsError={props.formState.errors.contextTwo}
        characterCount={CONTEXT_CHARACTER_LIMIT}
      />
    </>
  );
};

const PageThreeQuestions = (props: {
  control: Control<FormData>;
  formState: FormState<FormData>;
}) => {
  return (
    <>
      <FormsInputDropdown
        control={props.control}
        name='challengeThree'
        rules={{ required: CHALLENGE_ERROR_TEXT }}
        label='Third business challenge'
        placeholder='Select one'
        items={challengeDropdownItems}
        type='default'
        error={props.formState.errors.challengeThree}
        inputDisabled
      />
      <FormsMultilineInput
        control={props.control}
        name='contextThree'
        rules={{
          required: CONTEXT_ERROR_TEXT,
          maxLength: CONTEXT_CHARACTER_LIMIT,
        }}
        label={CONTEXT_INPUT_LABEL}
        aria-label='Tell us more about this challenge'
        placeholder={CONTEXT_INPUT_PLACEHOLDER}
        formsError={props.formState.errors.contextThree}
        characterCount={CONTEXT_CHARACTER_LIMIT}
      />
    </>
  );
};

const FormNavigation = (props: {
  currentPage: FormPage;
  formState: FormState<FormData>;
  handleSubmit: UseFormHandleSubmit<FormData>;
}) => {
  const { currentPage, handleSubmit } = props;
  // determine if we should provide a 'Back' button and what the previous page should be
  let previousPage: FormPage | undefined;
  switch (currentPage) {
    case 'two':
      previousPage = 'one';
      break;
    case 'three':
      previousPage = 'two';
      break;
    default:
      previousPage = undefined;
  }

  const businessChallengesMutator = Organizations.useCreateBusinessChallenges({
    mutation: {
      onMutate: () => {
        navigate(`${BUSINESS_CHALLENGES_HREF}${ROUTES.BUSINESS_CHALLENGES_SUBMITTING}`);
      },
      onSuccess: () => {
        navigate(`${BUSINESS_CHALLENGES_HREF}${ROUTES.BUSINESS_CHALLENGES_SUCCESS}`);
        clearLocalFormData();
      },
      onError: () => {
        navigate(`${BUSINESS_CHALLENGES_HREF}${ROUTES.BUSINESS_CHALLENGES_ERROR}`);
      },
    },
  });

  // form page submit handlers, these will be wrapped in RHF's handleSubmit
  // to ensure validation is run before progressing to next page or submitting data
  const onSubmit: SubmitHandler<FormData> = (data) => {
    if (currentPage === 'one') {
      updateLocalFormData(data);
      navigate(`${BUSINESS_CHALLENGES_HREF}${ROUTES.BUSINESS_CHALLENGES_TWO}`);
    }
    if (currentPage === 'two') {
      updateLocalFormData(data);
      navigate(`${BUSINESS_CHALLENGES_HREF}${ROUTES.BUSINESS_CHALLENGES_THREE}`);
    }
    if (currentPage === 'three') {
      if (
        data.challengeOne &&
        data.challengeTwo &&
        data.challengeThree &&
        data.contextOne &&
        data.contextTwo &&
        data.contextThree
      ) {
        businessChallengesMutator.mutate({
          data: {
            challengeOne: data.challengeOne,
            contextOne: data.contextOne,
            challengeTwo: data.challengeTwo,
            contextTwo: data.contextTwo,
            challengeThree: data.challengeThree,
            contextThree: data.contextThree,
          },
        });
      }
    }
  };

  return (
    <div className='fixed bottom-0 inset-x-0 flex gap-2 items-center justify-end bg-gray-50 py-6 px-8 h-16'>
      {previousPage && (
        <Button
          variant='secondary-gray'
          size='sm'
          href={`${BUSINESS_CHALLENGES_HREF}/${previousPage}`}
        >
          Back
        </Button>
      )}
      {currentPage === 'one' && (
        <Button variant='secondary-gray' size='sm' href={`/platform${ROUTES.DASHBOARD}`}>
          Back to dashboard
        </Button>
      )}
      {currentPage === 'error' || currentPage === 'success' ?
        <Button variant='primary' size='sm' href={`/platform${ROUTES.DASHBOARD}`}>
          Close
        </Button>
      : <Button
          variant='primary'
          size='sm'
          onPress={() => handleSubmit(onSubmit)()}
          isDisabled={currentPage === 'submitting'}
        >
          {currentPage === 'three' || currentPage === 'submitting' ? 'Submit' : 'Continue'}
        </Button>
      }
    </div>
  );
};

/** Ensure user doesn't navigate ahead of empty pages in form */
const getPageByData = ({
  paramPage,
  currentLocalFormData,
}: {
  paramPage: FormPage;
  currentLocalFormData?: FormData;
}): FormPage => {
  if (paramPage !== 'one' && paramPage !== 'success' && !currentLocalFormData?.challengeOne) {
    return 'one';
  } else if (paramPage === 'three' && !currentLocalFormData?.challengeTwo) {
    return 'two';
  } else {
    return paramPage;
  }
};

const getHeaderContent = (currentPage: FormPage) => {
  switch (currentPage) {
    case 'one':
      return {
        headerText: 'What are 3 challenges or opportunities your organization is facing?',
        subheaderText:
          'We use this to understand where your organization is headed, and how our network can help',
      };
    case 'two':
      return {
        headerText:
          'Tell us about a second key challenge or opportunity your organization is facing',
      };
    case 'three':
      return {
        headerText:
          'Tell us about a third key challenge or opportunity your organization is facing',
      };
    default:
      return undefined;
  }
};

const getExampleContent = (currentPage: FormPage) => {
  switch (currentPage) {
    case 'one':
      return {
        exampleHeader: 'Need help? See an example',
        exampleText: `Last year, we tested our program in a new rural region. While participation was high, engagement dropped off halfway through. We're reflecting on what didn't work and looking at how to adapt our approach before expanding further.`,
      };
    case 'two':
      return {
        exampleHeader: 'Need help? See an example',
        exampleText: `We've mainly relied on grants since we launched, but that's starting to limit our flexibility. We're now at a point where we want to understand how growing our revenue streams could fit into our business model, especially as we plan for long-term sustainability.`,
      };
    case 'three':
      return {
        exampleHeader: 'Need help? See an example',
        exampleText:
          'We are currently in a pivotal growth phase. After years of prototyping and validating our immersive educational experiences in Chile and internationally, we are now structuring a clear commercial strategy to scale.',
      };
    default:
      return undefined;
  }
};

const clearLocalFormData = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('businessNeedsFormData');
  }
};

const getLocalFormData = () => {
  if (typeof window !== 'undefined') {
    const localString = localStorage.getItem('businessNeedsFormData');
    const localData: FormData = JSON.parse(localString || '{}');
    return localData;
  }
};

const updateLocalFormData = (data: FormData) => {
  if (typeof window !== 'undefined') {
    const localData = JSON.parse(localStorage.getItem('businessNeedsFormData') || '{}');
    const updatedData: FormData = {
      ...localData,
      ...data,
    };
    localStorage.setItem('businessNeedsFormData', JSON.stringify(updatedData));
    return updatedData;
  }
};

export default BusinessChallengesForm;
